import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Swiper as SwiperType } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

import './GlacierVideoExploration.css';
import { images } from '../../../shared/assets';
import { useBrandHomePageImages } from '../../../../hooks/useBrandHomePageContent';
import { HandDrawnButton } from '../../../shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../../shared/CTALink/CTALink';
import { createSelfbookHandler } from '../../../../utils/selfbook';

interface DestinationData {
  id: number;
  image: string;
  tag: string;
  name: string;
  location: string;
  nights: string;
  link: string;
}

// Move destinations array inside component to use translations
const useDestinations = () => {
  const { t } = useTranslation();

  return [
    {
      id: 1,
      image: images.destinationsAtacama,
      tag: t('pages/home:exploration.destinations.atacama.tag'),
      name: t('pages/home:exploration.destinations.atacama.name'),
      location: t('pages/home:exploration.destinations.atacama.location'),
      nights: t('pages/home:exploration.destinations.atacama.nights'),
      link: "/atacama"
    },
    {
      id: 2,
      image: images.destinationsPatagonia,
      tag: t('pages/home:exploration.destinations.patagonia.tag'),
      name: t('pages/home:exploration.destinations.patagonia.name'),
      location: t('pages/home:exploration.destinations.patagonia.location'),
      nights: t('pages/home:exploration.destinations.patagonia.nights'),
      link: "/patagonia"
    },
    {
      id: 3,
      image: '/assets/images/experiences/experience-iguazu.jpg',
      tag: t('pages/home:exploration.destinations.iguazu.tag'),
      name: t('pages/home:exploration.destinations.iguazu.name'),
      location: t('pages/home:exploration.destinations.iguazu.location'),
      nights: t('pages/home:exploration.destinations.iguazu.nights'),
      link: "/iguazu"
    },
    {
      id: 4,
      image: images.destinationsMendoza,
      tag: t('pages/home:exploration.destinations.mendoza.tag'),
      name: t('pages/home:exploration.destinations.mendoza.name'),
      location: t('pages/home:exploration.destinations.mendoza.location'),
      nights: t('pages/home:exploration.destinations.mendoza.nights'),
      link: "/mendoza"
    },
    {
      id: 5,
      image: images.destinationsSantaCatarina,
      tag: t('pages/home:exploration.destinations.santaCatarina.tag'),
      name: t('pages/home:exploration.destinations.santaCatarina.name'),
      location: t('pages/home:exploration.destinations.santaCatarina.location'),
      nights: t('pages/home:exploration.destinations.santaCatarina.nights'),
      link: "/santa-catarina"
    }
  ];
};

interface TagProps {
  text: string;
}

const Tag: React.FC<TagProps> = ({ text }) => {
  return (
    <div className="glacier-video-exploration__tag">
      <div className="glacier-video-exploration__tag-border" />
      <div className="glacier-video-exploration__tag-text">
        <p>{text}</p>
      </div>
    </div>
  );
};

interface DestinationCardProps {
  destination: DestinationData;
  handleBooking: () => void;
}

const DestinationCard: React.FC<DestinationCardProps> = ({ destination, handleBooking }) => {
  const { t } = useTranslation();

  return (
    <div className="glacier-video-exploration__card">
      <div className="glacier-video-exploration__card-image-container">
        <img
          src={destination.image}
          alt={`${destination.name} ${destination.location}`}
          className="glacier-video-exploration__card-image"
        />
        <Tag text={destination.tag} />
      </div>
      <div className="glacier-video-exploration__card-content">
        <div className="glacier-video-exploration__card-title">
          <p>
            {destination.location === "Patagonia"
              ? destination.location
              : `${destination.name} ${destination.location}`}
          </p>
        </div>
        <div className="glacier-video-exploration__card-actions">

          <CTALink
            text={t('pages/home:buttons.visitTheProperty')}
            type="primary"
            color="awasi"
            href={destination.link}
            className="glacier-video-exploration__link"
          />
          <CTALink
            text={t('pages/home:buttons.bookYourStay')}
            type="secondary"
            color="awasi"
            onClick={handleBooking}
            className="glacier-video-exploration__link"
          />
        </div>
      </div>
    </div>
  );
};

export const GlacierVideoExploration: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const destinations = useDestinations();
  const carouselRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const handleBooking = createSelfbookHandler(navigate, '/contact/');

  const handleSeeDestinations = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleArrowClick = (direction: 'left' | 'right') => {
    if (swiperRef.current) {
      if (direction === 'left') {
        swiperRef.current?.slidePrev();
      } else {
        swiperRef.current?.slideNext();
      }
    }
  };

  // Use the glacier video URL from assets
  const glacierVideoUrl = images.destinationsBgVideo;

  // Ensure video plays
  React.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(err => {
        console.error('Glacier video autoplay failed:', err);
      });
    }
  }, [glacierVideoUrl]);

  return (
    <section className="glacier-video-exploration">
      {/* Meaningful Exploration Section Only */}
      <div className="glacier-video-exploration__content">
        <div className="glacier-video-exploration__text-content">
          <h2 className="glacier-video-exploration__title">
            {String(t('pages/home:exploration.title')).split(' ').map((word, index) => (
              <React.Fragment key={index}>
                {word}{' '}
                {index === 2 && <br />}
              </React.Fragment>
            ))}
          </h2>
          <div className="glacier-video-exploration__description-wrapper">
            <p className="glacier-video-exploration__description">
              {t('pages/home:exploration.description')}
            </p>
            {
              false &&
              <HandDrawnButton
                text={t('pages/home:exploration.cta')}
                onClick={handleSeeDestinations}
                variant="brand"
                color="awasi"
                animation="stroke-to-fill"
                size="normal"
                className="glacier-video-exploration__see-button"
              />
            }
          </div>
        </div>

        {/* Carousel Section */}
        <div className="glacier-video-exploration__carousel-container" ref={carouselRef}>
          <Swiper
            freeMode
            navigation
            spaceBetween={20}
            slidesPerView={1}
            className='glacier-video-exploration__carousel-swiper'
            breakpoints={{
              1024: {
                slidesPerView: 2,
                spaceBetween: 30,
              },
            }}
            onSwiper={(currentSwiper: SwiperType) => swiperRef.current = currentSwiper}
          >
            {destinations.map((destination) => (
              <SwiperSlide key={destination.id}>
                <DestinationCard destination={destination} handleBooking={handleBooking} />
              </SwiperSlide>
            ))}

          </Swiper>
          <div className="glacier-video-exploration__arrow left" onClick={() => handleArrowClick('left')}>
            <img
              src={images.carouselArrow}
              alt="Scroll to see more destinations"
              className="glacier-video-exploration__arrow-image"
            />
          </div>

          <div className="glacier-video-exploration__arrow" onClick={() => handleArrowClick('right')}>
            <img
              src={images.carouselArrow}
              alt="Scroll to see more destinations"
              className="glacier-video-exploration__arrow-image"
            />
          </div>
        </div>

      </div>
    </section>
  );
};